import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Download, Search, X, RefreshCw, Activity, User, Clock, Monitor } from "lucide-react";
import { format } from "date-fns";

const API_URL = "/api";

const ACTION_COLORS = {
  LOGIN: "bg-green-100 text-green-700 border-green-200",
  SSO_LOGIN: "bg-blue-100 text-blue-700 border-blue-200",
  GENERATE_INVOICE: "bg-purple-100 text-purple-700 border-purple-200",
  CANCEL_INVOICE: "bg-red-100 text-red-700 border-red-200",
  UPLOAD_AWB: "bg-orange-100 text-orange-700 border-orange-200",
  DOWNLOAD_PDF: "bg-indigo-100 text-indigo-700 border-indigo-200",
  CREATE_USER: "bg-teal-100 text-teal-700 border-teal-200",
  DELETE_USER: "bg-rose-100 text-rose-700 border-rose-200",
};

const MODULE_ICONS = {
  Auth: "🔐",
  Invoice: "📄",
  "User Management": "👥",
  Customer: "🏢",
  Pricelist: "💰",
};

const ActivityLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState({
    user_email: "",
    action: "",
    module: "",
    search: "",
    date_from: "",
    date_to: "",
  });

  const [users, setUsers] = useState([]);
  const limit = 50;
  const totalPages = Math.ceil(total / limit);

  const ACTIONS = [
    "LOGIN", "SSO_LOGIN", "GENERATE_INVOICE", "CANCEL_INVOICE",
    "UPLOAD_AWB", "DOWNLOAD_PDF", "CREATE_USER", "DELETE_USER",
  ];

  const MODULES = ["Auth", "Invoice", "User Management", "Customer", "Pricelist"];

  const fetchLogs = useCallback(async (pg = 1) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = { page: pg, limit, ...filters };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const res = await axios.get(`${API_URL}/activity-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLogs(res.data.data);
      setTotal(res.data.total);
      setPage(pg);
    } catch (err) {
      toast.error("Gagal memuat activity log");
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data || []);
    } catch {}
  };

  useEffect(() => {
    fetchLogs(1);
    fetchUsers();
  }, []);

  const handleFilter = () => fetchLogs(1);

  const handleReset = () => {
    setFilters({ user_email: "", action: "", module: "", search: "", date_from: "", date_to: "" });
    setTimeout(() => fetchLogs(1), 100);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem("token");
      const params = { ...filters };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const res = await axios.get(`${API_URL}/activity-logs/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `activity_log_${format(new Date(), "ddMMyyyy_HHmm")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV berhasil didownload");
    } catch {
      toast.error("Gagal export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const hasFilters = Object.values(filters).some((v) => v);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="bg-slate-900 p-2.5 rounded-xl">
            <Activity className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900">Activity Log</h1>
            <p className="text-sm text-slate-500">Rekam jejak aktivitas seluruh pengguna sistem</p>
          </div>
          <div className="ml-2 flex items-center gap-1 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
            <Shield size={12} className="text-amber-600" />
            <span className="text-xs font-bold text-amber-700">ADMIN ONLY</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchLogs(page)}
            className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-lg font-bold hover:bg-slate-50 flex items-center gap-2 transition shadow-sm"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            onClick={handleExportCSV}
            disabled={isExporting}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-slate-800 flex items-center gap-2 transition shadow-sm"
          >
            <Download size={16} /> {isExporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Total Log", value: total.toLocaleString(), icon: Activity, color: "bg-slate-900 text-white" },
          { label: "Tampil", value: logs.length, icon: Monitor, color: "bg-blue-600 text-white" },
          { label: "Halaman", value: `${page}/${totalPages || 1}`, icon: Clock, color: "bg-purple-600 text-white" },
          { label: "Filter Aktif", value: Object.values(filters).filter(Boolean).length, icon: Search, color: "bg-amber-500 text-white" },
        ].map((s) => (
          <div key={s.label} className={`${s.color} p-4 rounded-2xl flex items-center gap-3 shadow-sm`}>
            <s.icon size={20} className="opacity-80" />
            <div>
              <div className="text-2xl font-black leading-none">{s.value}</div>
              <div className="text-xs opacity-70 mt-0.5">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* User */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">User</label>
            <select
              value={filters.user_email}
              onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-slate-400"
            >
              <option value="">Semua User</option>
              {users.map((u) => (
                <option key={u.id} value={u.email}>{u.email}</option>
              ))}
            </select>
          </div>

          {/* Action */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Aksi</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-slate-400"
            >
              <option value="">Semua Aksi</option>
              {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          {/* Module */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Modul</label>
            <select
              value={filters.module}
              onChange={(e) => setFilters({ ...filters, module: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-slate-400"
            >
              <option value="">Semua Modul</option>
              {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Dari Tanggal</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-slate-400"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Sampai Tanggal</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-slate-400"
            />
          </div>

          {/* Search */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Cari</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Keyword..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleFilter()}
                className="w-full pl-8 p-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:border-slate-400"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleFilter}
            className="bg-slate-900 text-white px-5 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition"
          >
            Filter
          </button>
          {hasFilters && (
            <button
              onClick={handleReset}
              className="bg-red-50 text-red-600 border border-red-200 px-5 py-2 rounded-lg font-bold text-sm hover:bg-red-100 transition flex items-center gap-1"
            >
              <X size={14} /> Reset
            </button>
          )}
          <span className="text-xs text-slate-400 self-center ml-2">
            {total.toLocaleString()} hasil ditemukan
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-white">
            <tr>
              <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wider">Waktu</th>
              <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wider">User</th>
              <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wider">Aksi</th>
              <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wider">Modul</th>
              <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wider">Detail</th>
              <th className="px-4 py-3 text-left font-bold text-xs uppercase tracking-wider">IP</th>
              <th className="px-4 py-3 text-center font-bold text-xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan="7" className="p-16 text-center text-slate-400">
                  <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                  Memuat data...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-16 text-center text-slate-400">
                  <Activity className="mx-auto mb-2 opacity-30" size={32} />
                  Belum ada aktivitas tercatat
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-mono text-xs text-slate-600">
                      {log.created_at ? new Date(log.created_at).toLocaleString("id-ID") : "-"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {(log.user_email || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold text-slate-800 text-xs">{log.user_name || "-"}</div>
                        <div className="text-xs text-slate-400">{log.user_email || "System"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full border ${ACTION_COLORS[log.action] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-slate-600">
                      {MODULE_ICONS[log.module] || "📌"} {log.module || "-"}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="text-xs text-slate-700 truncate" title={log.detail}>{log.detail || "-"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-slate-500">{log.ip_address || "-"}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${log.status === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex justify-between items-center">
            <span className="text-sm text-slate-500">
              Halaman <b>{page}</b> dari <b>{totalPages}</b> — {total.toLocaleString()} total log
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => fetchLogs(page - 1)}
                disabled={page === 1}
                className="px-4 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-bold disabled:opacity-40 hover:bg-slate-50 transition"
              >
                Prev
              </button>
              <button
                onClick={() => fetchLogs(page + 1)}
                disabled={page === totalPages}
                className="px-4 py-1.5 bg-slate-900 text-white rounded-lg text-sm font-bold disabled:opacity-40 hover:bg-slate-800 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogPage;